import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '../sheet';

import { Button } from '../button';
import { SelectCustom } from './SelectCustom';
import {
  MONTH_OPTIONS,
  MONTH_QUARTERLY_OPTIONS,
  PeriodSelector,
} from '../../constants/date';
import { FieldItem } from './FieldItem';
import { useForm } from '@tanstack/react-form';
import z from 'zod';
import dayjs from 'dayjs';
import { useRef, useState } from 'react';
import { type FormValidator, validateFormValue } from '../../utils/form';

const schema = z.object({
  period: z.enum([
    PeriodSelector.Monthly,
    PeriodSelector.Quarterly,
    PeriodSelector.Annually,
  ]),
  year: z.string().min(1, 'This field is required'),
  month: z.string().nullable().optional(),
});

const monthValidator: FormValidator<z.infer<typeof schema>> = ({ value }) => {
  if (value.period !== PeriodSelector.Annually && !value.month) {
    return {
      error: { message: 'This field is required' },
      path: 'month',
    };
  }
};

type FormValueType = z.infer<typeof schema>;

type RangeSelectorProps = {
  value: FormValueType;
  onChange: (value: FormValueType) => void;
  icon: React.ReactNode;
  title: string;
  /** Format for display value ex: MMM YYYY*/
  format?: string;
};

export function RangeSelector({
  value,
  onChange,
  icon,
  title,
  format = 'MMM YYYY',
}: RangeSelectorProps) {
  const [open, setOpen] = useState(false);
  // Radix Select fires onValueChange when options change and the current value
  // no longer matches any item (e.g. switching period from Quarterly to Monthly).
  // This spurious emission overwrites the month field during form.reset().
  // We use this flag to ignore onValueChange in the month field during reset.
  const isResettingRef = useRef(false);

  const form = useForm({
    defaultValues: {
      period: value.period || PeriodSelector.Monthly,
      year: value.year || String(dayjs().year()),
      month: value.month,
    } as FormValueType,
    onSubmit: async ({ value }) => {
      handleSubmit(value);
    },
    validators: {
      onChange: validateFormValue(schema, [monthValidator]),
    },
  });

  const handleSubmit = (value: FormValueType) => {
    onChange(value);
    setOpen(false);
  };

  const getYearOptionsAroundCurrent = (
    pastYears = 10,
    futureYears = 5,
  ): Array<{ label: string; value: string }> => {
    const currentYear = dayjs().year();
    const startYear = currentYear - pastYears;
    const totalYears = pastYears + futureYears + 1;

    return Array.from({ length: totalYears }, (_, index) => {
      const year = startYear + index;
      return {
        label: String(year),
        value: String(year),
      };
    });
  };

  const getSelectorLabel = () => {
    const period = form.getFieldValue('period');
    if (period === PeriodSelector.Quarterly) {
      return `Q${value.month} ${value.year}`;
    }

    if (period === PeriodSelector.Monthly) {
      const monthIndex = MONTH_OPTIONS.findIndex(m => m.value === value.month);
      if (monthIndex === -1) {
        return '';
      }
      return dayjs(new Date(Number(value.year), monthIndex, 1)).format(format);
    }

    return dayjs(`${value.year}`).format('YYYY');
  };

  const resetValue = () => {
    isResettingRef.current = true;
    form.reset({
      period: value.period || PeriodSelector.Monthly,
      year: value.year || String(dayjs().year()),
      month: value.month,
    });
    requestAnimationFrame(() => {
      isResettingRef.current = false;
    });
  };

  return (
    <Sheet
      open={open}
      onOpenChange={open => {
        if (!open) {
          resetValue();
        }
        setOpen(open);
      }}
    >
      <SheetTrigger asChild>
        <Button variant="outline">
          {icon}
          <div>{getSelectorLabel()}</div>
        </Button>
      </SheetTrigger>
      <SheetContent className="min-w-full md:min-w-[45vw] md:rounded-lg flex flex-col">
        <SheetHeader className="pb-10">
          <SheetTitle className="text-2xl font-bold">{title}</SheetTitle>
        </SheetHeader>
        <div className="flex-1">
          <form
            onSubmit={e => {
              e.preventDefault();
              form.handleSubmit();
            }}
            className="grid grid-cols-2 gap-6"
          >
            <form.Field name="period">
              {field => (
                <FieldItem field={field} label="Period" className="col-span-2">
                  <SelectCustom
                    value={field.state.value}
                    onChange={value => {
                      field.handleChange(value);
                      form.setFieldValue('month', null);
                    }}
                    options={[
                      {
                        label: PeriodSelector.Monthly,
                        value: PeriodSelector.Monthly,
                      },
                      {
                        label: PeriodSelector.Quarterly,
                        value: PeriodSelector.Quarterly,
                      },
                      {
                        label: PeriodSelector.Annually,
                        value: PeriodSelector.Annually,
                      },
                    ]}
                  />
                </FieldItem>
              )}
            </form.Field>

            <form.Field name="year">
              {field => (
                <FieldItem field={field} label="Year">
                  <SelectCustom
                    value={field.state.value}
                    onChange={field.handleChange}
                    options={getYearOptionsAroundCurrent()}
                  />
                </FieldItem>
              )}
            </form.Field>

            <form.Subscribe
              selector={state => ({
                period: state.values.period,
              })}
            >
              {({ period }) =>
                period !== PeriodSelector.Annually && (
                  <form.Field name="month">
                    {field => (
                      <FieldItem
                        field={field}
                        label={
                          period === PeriodSelector.Quarterly
                            ? 'Quarter'
                            : 'Month'
                        }
                      >
                        <SelectCustom
                          // Guard against Radix's spurious onValueChange during reset.
                          // See isResettingRef comment above for details.
                          onChange={val => {
                            if (isResettingRef.current) {
                              return;
                            }
                            field.handleChange(val);
                          }}
                          options={
                            period === PeriodSelector.Quarterly
                              ? MONTH_QUARTERLY_OPTIONS
                              : MONTH_OPTIONS
                          }
                          value={field.state.value}
                        />
                      </FieldItem>
                    )}
                  </form.Field>
                )
              }
            </form.Subscribe>
          </form>
        </div>

        <SheetFooter className="w-full flex gap-2">
          <Button variant="outline" className="w-full" onClick={resetValue}>
            Reset
          </Button>
          <Button
            type="submit"
            className="w-full"
            onClick={() => {
              form.handleSubmit();
            }}
          >
            Apply
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
